import React, { useRef, useState } from 'react';
import { FileText, Loader2, X, Truck, CheckSquare, Edit3, AlertTriangle, Upload, ExternalLink, Check, Plus } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import ProductSearchInput from '../ui/ProductSearchInput';

// ==================== Dispatch Note Dialog (Step 3 — Vehicle No only) ====================
export const OCDispatchDialog = ({
  open, onOpenChange, dispatchOC,
  vehicleNumber, setVehicleNumber, dispatchNotes, setDispatchNotes,
  dispatchLoading, onConfirm,
}) => {
  const allItems = (dispatchOC?.items && dispatchOC.items.length > 0) ? dispatchOC.items : (dispatchOC?.signage_items || []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="dispatch-note-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="w-4 h-4 text-amber-600" /> Dispatch Note — {dispatchOC?.oc_number}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* OC Summary */}
          {dispatchOC && (
            <div className="p-3 bg-gray-50 rounded-lg text-xs space-y-1">
              <div className="flex justify-between"><span className="text-gray-500">Customer</span><span className="font-semibold">{dispatchOC.customer?.name}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Branch</span><span className="font-semibold">{dispatchOC.branch_name || dispatchOC.branch || '-'}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">OC Value</span><span className="font-semibold">INR {(dispatchOC.total || 0).toLocaleString()}</span></div>
            </div>
          )}

          {/* Vehicle Number */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Vehicle Number <span className="text-red-500">*</span></label>
            <Input value={vehicleNumber} onChange={e => setVehicleNumber(e.target.value.toUpperCase())}
              placeholder="e.g. MH02AB1234" data-testid="vehicle-number-input" className="font-mono" />
          </div>

          {/* Items (read-only) */}
          {allItems.length > 0 && (
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1.5 block">Items ({allItems.length})</label>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="text-[9px] font-bold text-gray-500 uppercase bg-gray-50">
                      <th className="text-left px-3 py-2">Product</th>
                      <th className="text-right px-3 py-2 w-20">Qty</th>
                      <th className="text-left px-3 py-2 w-14">Unit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allItems.map((item, i) => (
                      <tr key={i} className="border-t border-gray-100" data-testid={`dispatch-item-${i}`}>
                        <td className="px-3 py-2">
                          <p className="text-xs font-medium truncate">{item.product_name || `${item.shape || ''} ${item.dimensions || ''}`}</p>
                          {item.sku && <p className="text-[9px] text-gray-400">SKU: {item.sku}</p>}
                        </td>
                        <td className="px-3 py-2 text-xs text-right font-semibold">{item.qty}</td>
                        <td className="px-3 py-2 text-[10px] text-gray-500">{item.unit || 'PCS'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Notes (optional)</label>
            <Textarea rows={2} value={dispatchNotes} onChange={e => setDispatchNotes(e.target.value)}
              placeholder="Any special instructions..." data-testid="dispatch-notes" />
          </div>

          <div className="p-3 bg-blue-50 rounded-lg text-[10px] text-blue-700">
            <strong>Note:</strong> A Dispatch Note will be created in "Awaiting Invoice" status. Stock will be deducted only after the invoice is uploaded and saved by the Accounts team.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onConfirm} disabled={dispatchLoading || !vehicleNumber.trim()}
            data-testid="confirm-dispatch-btn" className="bg-amber-600 hover:bg-amber-700 gap-1.5">
            {dispatchLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Truck className="w-3.5 h-3.5" />}
            Create Dispatch Note
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// ==================== Invoice Dialog ====================
export const OCInvoiceDialog = ({
  open, onOpenChange, invoiceOC,
  invoiceNumber, setInvoiceNumber, invoiceDate, setInvoiceDate,
  invoiceNotes, setInvoiceNotes, invoiceLoading, onConfirm,
  invoiceDocUrl, setInvoiceDocUrl, invoiceDocName, setInvoiceDocName,
}) => {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const { fileAPI } = await import('../../services/api');
      const result = await fileAPI.uploadDocument(file);
      setInvoiceDocUrl(result.file_path);
      setInvoiceDocName(file.name);
    } catch (err) {
      console.error('Upload failed:', err);
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const clearDoc = () => { setInvoiceDocUrl(''); setInvoiceDocName(''); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="invoice-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-4 h-4" style={{ color: '#24549c' }} /> Tally Invoice — {invoiceOC?.oc_number}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {invoiceOC && (
            <div className="p-3 bg-gray-50 rounded-lg text-xs">
              <div className="flex justify-between"><span className="text-gray-500">Customer</span><span className="font-semibold">{invoiceOC.customer?.name}</span></div>
              <div className="flex justify-between mt-1"><span className="text-gray-500">OC Value</span><span className="font-semibold">INR {(invoiceOC.total || 0).toLocaleString()}</span></div>
              {invoiceOC.vehicle_number && <div className="flex justify-between mt-1"><span className="text-gray-500">Vehicle</span><span className="font-semibold text-amber-700">{invoiceOC.vehicle_number}</span></div>}
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Tally Invoice Number <span className="text-red-500">*</span></label>
            <Input value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)} placeholder="e.g. INV-2026-0001" data-testid="tally-invoice-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Invoice Date</label>
            <Input type="date" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} data-testid="tally-invoice-date" />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Upload Invoice Document <span className="text-red-500">*</span></label>
            <input type="file" ref={fileRef} className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx" onChange={handleFileUpload} />
            {invoiceDocUrl ? (
              <div className="flex items-center gap-2 p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg">
                <FileText className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                <span className="text-xs font-medium text-gray-700 truncate flex-1">{invoiceDocName || 'Invoice Document'}</span>
                <button onClick={clearDoc} className="p-0.5 rounded hover:bg-emerald-100" data-testid="clear-invoice-doc">
                  <X className="w-3 h-3 text-gray-500" />
                </button>
              </div>
            ) : (
              <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-invoice-doc-btn"
                className="w-full flex items-center justify-center gap-2 px-3 py-2.5 text-xs border border-dashed border-gray-300 rounded-lg hover:border-blue-400 text-gray-500 disabled:opacity-50">
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {uploading ? 'Uploading...' : 'Click to upload invoice PDF / image'}
              </button>
            )}
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Notes (optional)</label>
            <Textarea rows={2} value={invoiceNotes} onChange={e => setInvoiceNotes(e.target.value)} placeholder="Any invoice notes..." data-testid="tally-invoice-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onConfirm} disabled={invoiceLoading || !invoiceNumber.trim() || !invoiceDocUrl} data-testid="confirm-invoice-btn" className="bg-[#24549c] hover:bg-[#1a3f7a] gap-1.5">
            {invoiceLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
            Save Invoice
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// ==================== Amendment Dialog ====================
export const OCAmendDialog = ({
  open, onOpenChange, amendOC,
  amendItems, setAmendItems, amendReason, setAmendReason,
  amendDetails, setAmendDetails,
  amendLoading, onSubmit,
  customers = [],
  branches = [],
  billingAddresses = [],
}) => {
  const updateDetail = (field, val) => setAmendDetails(prev => ({ ...prev, [field]: val }));
  const updateTerms = (field, val) => setAmendDetails(prev => ({ ...prev, terms: { ...(prev.terms || {}), [field]: val } }));
  const updateShipTo = (field, val) => setAmendDetails(prev => ({ ...prev, ship_to: { ...(prev.ship_to || {}), [field]: val } }));

  const [customerQuery, setCustomerQuery] = useState('');
  const currentCustomerId = amendDetails.customer?.id || amendOC?.customer?.id || '';
  const currentCustomerName = amendDetails.customer?.name || amendOC?.customer?.name || '';
  const customerChanged = !!(amendDetails.customer && amendOC && amendDetails.customer.id && amendDetails.customer.id !== amendOC.customer?.id);
  const filteredCustomers = customerQuery.trim()
    ? (customers || []).filter(c => (c.name || '').toLowerCase().includes(customerQuery.trim().toLowerCase())).slice(0, 8)
    : [];
  const pickCustomer = (c) => {
    setAmendDetails(prev => ({
      ...prev,
      customer: {
        id: c.id, name: c.name,
        address: c.address || '', city: c.city || '', state: c.state || '',
        pincode: c.pincode || '', gstin: c.gstin || '', pan: c.pan || '',
        phone: c.phone || '', email: c.email || '', contact_person: c.contact || '',
      },
    }));
    setCustomerQuery('');
  };
  const resetCustomer = () => setAmendDetails(prev => { const n = { ...prev }; delete n.customer; return n; });
  const hasTallyInvoice = !!(amendOC?.tally_invoice_number);

  // --- Ship-from branch change ---
  const currentBranchId = amendOC?.branch || '';
  const currentBranchName = amendOC?.branch_name || (branches.find(b => b.id === currentBranchId)?.name) || '-';
  const proposedBranchId = amendDetails.branch || '';
  const branchChanged = !!(proposedBranchId && proposedBranchId !== currentBranchId);
  const proposedBranch = branches.find(b => b.id === proposedBranchId);
  const currentBranchObj = branches.find(b => b.id === currentBranchId);
  const branchStateChanged = branchChanged && (proposedBranch?.state || '').toLowerCase() !== (currentBranchObj?.state || '').toLowerCase();
  const setBranch = (bid) => {
    setAmendDetails(prev => {
      const n = { ...prev };
      if (!bid || bid === currentBranchId) {
        delete n.branch;
      } else {
        n.branch = bid;
      }
      return n;
    });
  };

  // --- Billing Address (Ship-From address block on PDF) change ---
  const currentBillingAddress = amendOC?.billing_address || null;
  const currentBillingId = currentBillingAddress?.id || '';
  const proposedBillingAddress = amendDetails.billing_address || null;
  const proposedBillingId = proposedBillingAddress?.id || '';
  const billingChanged = !!(proposedBillingAddress && proposedBillingId && proposedBillingId !== currentBillingId);
  const billingStateChanged = billingChanged &&
    (proposedBillingAddress?.state || '').toLowerCase() !== (currentBillingAddress?.state || '').toLowerCase();
  const setBillingById = (baId) => {
    setAmendDetails(prev => {
      const n = { ...prev };
      if (!baId || baId === currentBillingId) {
        delete n.billing_address;
      } else {
        const ba = (billingAddresses || []).find(b => b.id === baId);
        if (ba) {
          n.billing_address = {
            id: ba.id,
            name: ba.name || '',
            branch_name: ba.branch_name || ba.name || '',
            address: ba.address || '',
            city: ba.city || '',
            state: ba.state || '',
            pincode: ba.pincode || '',
            gstin: ba.gstin || '',
          };
        }
      }
      return n;
    });
  };
  const resetBilling = () => setAmendDetails(prev => { const n = { ...prev }; delete n.billing_address; return n; });

  return (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="amend-dialog">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Edit3 className="w-4 h-4 text-purple-600" /> Amend OC — {amendOC?.oc_number}
          {(amendOC?.revision || 0) > 0 && <span className="text-[10px] font-bold text-purple-600 bg-purple-100 px-1.5 py-0.5 rounded-full">Rev-{amendOC.revision}</span>}
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        {amendOC && (
          <div className="p-3 bg-gray-50 rounded-lg text-xs">
            <div className="flex justify-between"><span className="text-gray-500">Customer</span><span className="font-semibold">{amendOC.customer?.name}</span></div>
            <div className="flex justify-between mt-1"><span className="text-gray-500">Current Status</span><span className="font-semibold">{amendOC.status}</span></div>
            <div className="flex justify-between mt-1"><span className="text-gray-500">Current Total</span><span className="font-semibold">INR {(amendOC.total || 0).toLocaleString()}</span></div>
          </div>
        )}

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-[11px] text-amber-800">This amendment will be submitted for approval. Changes won&apos;t take effect until approved by Admin/Manager.</p>
        </div>

        {/* Customer change (optional) */}
        <div data-testid="amend-customer-section">
          <label className="text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-2 block">Customer</label>
          <div className="border border-gray-200 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between text-xs">
              <div>
                <div className="text-[10px] text-gray-400 uppercase tracking-wider">Current</div>
                <div className="font-semibold text-gray-800">{amendOC?.customer?.name || '-'}</div>
              </div>
              {customerChanged && (
                <div className="text-right">
                  <div className="text-[10px] text-purple-500 uppercase tracking-wider">Proposed</div>
                  <div className="font-semibold text-purple-700" data-testid="amend-customer-new-name">{amendDetails.customer?.name}</div>
                </div>
              )}
            </div>
            <div className="relative">
              <Input
                placeholder="Search customer by name to change… (leave empty to keep current)"
                value={customerChanged ? currentCustomerName : customerQuery}
                onChange={e => {
                  if (customerChanged) resetCustomer();
                  setCustomerQuery(e.target.value);
                }}
                className="text-xs"
                data-testid="amend-customer-search"
              />
              {filteredCustomers.length > 0 && !customerChanged && (
                <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
                  {filteredCustomers.map(c => (
                    <button
                      key={c.id}
                      onClick={() => pickCustomer(c)}
                      className="w-full text-left px-3 py-2 hover:bg-purple-50 text-xs border-b last:border-b-0"
                      data-testid={`amend-customer-option-${c.id}`}
                    >
                      <div className="font-semibold text-gray-800">{c.name}</div>
                      <div className="text-[10px] text-gray-500">{[c.city, c.state, c.gstin].filter(Boolean).join(' · ')}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {customerChanged && (
              <div className="flex items-center justify-between text-[10px] pt-1">
                <button
                  onClick={resetCustomer}
                  className="text-red-600 hover:text-red-700 font-semibold"
                  data-testid="amend-customer-reset"
                >
                  Reset to current customer
                </button>
                {hasTallyInvoice && (
                  <span className="text-amber-700 font-semibold">⚠ Tally invoice will be cleared on approval</span>
                )}
              </div>
            )}
            {customerChanged && (
              <div className="text-[10px] text-gray-500 bg-gray-50 rounded p-2">
                GST will be recomputed (intra-state ↔ inter-state) based on the new customer&apos;s state.
              </div>
            )}
          </div>
        </div>

        {/* Ship-From Branch change (optional) */}
        <div data-testid="amend-branch-section">
          <label className="text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-2 block">Ship-From Branch</label>
          <div className="border border-gray-200 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between text-xs">
              <div>
                <div className="text-[10px] text-gray-400 uppercase tracking-wider">Current</div>
                <div className="font-semibold text-gray-800">{currentBranchName}</div>
                {currentBranchObj?.state && <div className="text-[10px] text-gray-500">{currentBranchObj.state}</div>}
              </div>
              {branchChanged && (
                <div className="text-right">
                  <div className="text-[10px] text-purple-500 uppercase tracking-wider">Proposed</div>
                  <div className="font-semibold text-purple-700" data-testid="amend-branch-new-name">{proposedBranch?.name}</div>
                  {proposedBranch?.state && <div className="text-[10px] text-purple-500">{proposedBranch.state}</div>}
                </div>
              )}
            </div>
            <select
              value={proposedBranchId || currentBranchId}
              onChange={e => setBranch(e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-purple-200"
              data-testid="amend-branch-select"
            >
              <option value={currentBranchId}>— Keep current ({currentBranchName}) —</option>
              {branches.filter(b => b.id !== currentBranchId).map(b => (
                <option key={b.id} value={b.id}>{b.name}{b.state ? ` · ${b.state}` : ''}</option>
              ))}
            </select>
            {branchChanged && (
              <div className="flex items-center justify-between text-[10px] pt-1">
                <button
                  onClick={() => setBranch('')}
                  className="text-red-600 hover:text-red-700 font-semibold"
                  data-testid="amend-branch-reset"
                >
                  Reset to current branch
                </button>
                {hasTallyInvoice && (
                  <span className="text-amber-700 font-semibold">⚠ Tally invoice will be cleared on approval</span>
                )}
              </div>
            )}
            {branchChanged && branchStateChanged && (
              <div className="text-[10px] text-gray-500 bg-gray-50 rounded p-2">
                GST will be recomputed (intra-state ↔ inter-state) since the new ship-from branch is in a different state.
              </div>
            )}
          </div>
        </div>

        {/* Ship-From Billing Address (independent of branch — shown on PDF) */}
        <div data-testid="amend-billing-address-section">
          <label className="text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-2 block">
            Ship-From Billing Address
            <span className="ml-2 text-[9px] font-normal text-gray-400 normal-case">(the address printed on the invoice "Ship From" block)</span>
          </label>
          <div className="border border-gray-200 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between text-xs">
              <div className="flex-1 min-w-0">
                <div className="text-[10px] text-gray-400 uppercase tracking-wider">Current</div>
                <div className="font-semibold text-gray-800 truncate">{currentBillingAddress?.name || currentBillingAddress?.branch_name || '-'}</div>
                {currentBillingAddress && (
                  <div className="text-[10px] text-gray-500 truncate">
                    {[currentBillingAddress.city, currentBillingAddress.state].filter(Boolean).join(', ')}
                    {currentBillingAddress.gstin ? ` · ${currentBillingAddress.gstin}` : ''}
                  </div>
                )}
              </div>
              {billingChanged && (
                <div className="text-right flex-1 min-w-0 pl-3">
                  <div className="text-[10px] text-purple-500 uppercase tracking-wider">Proposed</div>
                  <div className="font-semibold text-purple-700 truncate" data-testid="amend-billing-new-name">{proposedBillingAddress?.name || proposedBillingAddress?.branch_name}</div>
                  <div className="text-[10px] text-purple-500 truncate">
                    {[proposedBillingAddress?.city, proposedBillingAddress?.state].filter(Boolean).join(', ')}
                    {proposedBillingAddress?.gstin ? ` · ${proposedBillingAddress.gstin}` : ''}
                  </div>
                </div>
              )}
            </div>
            <select
              value={proposedBillingId || currentBillingId}
              onChange={e => setBillingById(e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-purple-200"
              data-testid="amend-billing-select"
            >
              <option value={currentBillingId}>— Keep current ({currentBillingAddress?.name || '-'}) —</option>
              {(billingAddresses || []).filter(b => b.id !== currentBillingId).map(b => (
                <option key={b.id} value={b.id}>{b.name}{b.city ? ` · ${b.city}` : ''}{b.state ? `, ${b.state}` : ''}</option>
              ))}
            </select>
            {(billingAddresses || []).length === 0 && (
              <p className="text-[10px] text-gray-500">No saved billing addresses found. Add one from Sales → Billing Addresses.</p>
            )}
            {billingChanged && (
              <div className="flex items-center justify-between text-[10px] pt-1">
                <button
                  onClick={resetBilling}
                  className="text-red-600 hover:text-red-700 font-semibold"
                  data-testid="amend-billing-reset"
                >
                  Reset to current address
                </button>
                {hasTallyInvoice && (
                  <span className="text-amber-700 font-semibold">⚠ Tally invoice will be cleared on approval</span>
                )}
              </div>
            )}
            {billingChanged && billingStateChanged && (
              <div className="text-[10px] text-gray-500 bg-gray-50 rounded p-2">
                GST will be recomputed (intra-state ↔ inter-state) since the new billing address is in a different state.
              </div>
            )}
          </div>
        </div>

        {/* Editable Items Table */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">Items (Edit Qty &amp; Rate)</label>
            <button
              data-testid="amend-add-item"
              onClick={() => setAmendItems(prev => [...prev, { product_id: '', product_name: '', description: '', unit: 'PCS', qty: 0, rate: 0, discount: 0, amount: 0, hsn_code: '', _isNew: true }])}
              className="flex items-center gap-1 px-2 py-1 text-[10px] font-semibold rounded-md hover:bg-purple-50" style={{ color: '#7c3aed' }}>
              <Plus className="w-3 h-3" /> Add Item
            </button>
          </div>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="text-[9px] font-bold text-gray-500 uppercase bg-gray-50">
                  <th className="text-left px-3 py-2">#</th>
                  <th className="text-left px-3 py-2">Product</th>
                  <th className="text-left px-3 py-2 w-20">Unit</th>
                  <th className="text-right px-3 py-2 w-20">Qty</th>
                  <th className="text-right px-3 py-2 w-24">Rate</th>
                  <th className="text-right px-3 py-2 w-20">Disc %</th>
                  <th className="text-right px-3 py-2 w-28">Amount</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {amendItems.map((item, idx) => (
                  <tr key={idx} className="border-t border-gray-100">
                    <td className="px-3 py-2 text-[10px] text-gray-400">{idx + 1}</td>
                    <td className="px-3 py-2 text-xs font-medium text-gray-800">
                      {item._isNew ? (
                        <ProductSearchInput
                          value={item.product_name || ''}
                          productId={item.product_id || ''}
                          branchId={currentBranchId}
                          onSelect={(p) => {
                            const items = [...amendItems];
                            items[idx] = { ...items[idx], product_id: p.id, product_name: p.name, description: p.description || '', unit: p.sell_unit || p.unit || 'PCS', hsn_code: p.hsn_code || '', rate: p.selling_price || 0, _isNew: false };
                            items[idx].amount = (items[idx].qty || 0) * items[idx].rate;
                            setAmendItems(items);
                          }}
                          onClear={() => {
                            const items = [...amendItems];
                            items[idx] = { ...items[idx], product_id: '', product_name: '', description: '', unit: 'PCS', hsn_code: '', rate: 0, amount: 0, _isNew: true };
                            setAmendItems(items);
                          }}
                          placeholder="Search product..."
                          testId={`amend-product-${idx}`}
                        />
                      ) : (
                        item.product_name || item.description || '-'
                      )}
                    </td>
                    <td className="px-3 py-2 text-[10px] text-gray-500">{item.unit || 'PCS'}</td>
                    <td className="px-3 py-2">
                      <input type="number" value={item.qty || 0} min={0}
                        data-testid={`amend-qty-${idx}`}
                        onChange={e => {
                          const items = [...amendItems];
                          const q = parseFloat(e.target.value) || 0;
                          const r = items[idx].rate || 0;
                          const d = items[idx].discount || 0;
                          items[idx] = { ...items[idx], qty: q, amount: Math.round(q * r * (1 - d / 100)) };
                          setAmendItems(items);
                        }}
                        className="w-full text-right px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-purple-200 focus:outline-none" />
                    </td>
                    <td className="px-3 py-2">
                      <input type="number" value={item.rate || 0} min={0}
                        data-testid={`amend-rate-${idx}`}
                        onChange={e => {
                          const items = [...amendItems];
                          const r = parseFloat(e.target.value) || 0;
                          const q = items[idx].qty || 0;
                          const d = items[idx].discount || 0;
                          items[idx] = { ...items[idx], rate: r, amount: Math.round(q * r * (1 - d / 100)) };
                          setAmendItems(items);
                        }}
                        className="w-full text-right px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-purple-200 focus:outline-none" />
                    </td>
                    <td className="px-3 py-2">
                      <input type="number" value={item.discount || 0} min={0} max={100}
                        data-testid={`amend-discount-${idx}`}
                        onChange={e => {
                          const items = [...amendItems];
                          const d = parseFloat(e.target.value) || 0;
                          const q = items[idx].qty || 0;
                          const r = items[idx].rate || 0;
                          items[idx] = { ...items[idx], discount: d, amount: Math.round(q * r * (1 - d / 100)) };
                          setAmendItems(items);
                        }}
                        className="w-full text-right px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-purple-200 focus:outline-none" />
                    </td>
                    <td className="px-3 py-2 text-xs text-right font-semibold text-gray-800">
                      {Math.round((item.qty || 0) * (item.rate || 0) * (1 - (item.discount || 0) / 100)).toLocaleString('en-IN')}
                    </td>
                    <td className="px-3 py-2">
                      {amendItems.length > 1 && (
                        <button onClick={() => setAmendItems(amendItems.filter((_, i) => i !== idx))} className="p-0.5 rounded hover:bg-red-50">
                          <X className="w-3 h-3 text-red-400" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-gray-200 bg-gray-50">
                  <td colSpan={6} className="px-3 py-2 text-xs font-bold text-gray-700 text-right">Proposed Subtotal:</td>
                  <td className="px-3 py-2 text-xs font-bold text-right" style={{ color: '#24549c' }}>
                    INR {amendItems.reduce((s, i) => s + Math.round((i.qty || 0) * (i.rate || 0) * (1 - (i.discount || 0) / 100)), 0).toLocaleString('en-IN')}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Other Charges (Freight, Packing, etc.) */}
        <div data-testid="amend-other-charges-section">
          <div className="flex items-center justify-between mb-2">
            <label className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">Other Charges (Freight, Packing, etc.)</label>
            <button
              data-testid="amend-add-charge"
              onClick={() => setAmendDetails(prev => ({ ...prev, other_charges: [...(prev.other_charges || []), { description: '', amount: 0, taxable: true }] }))}
              className="flex items-center gap-1 px-2 py-1 text-[10px] font-semibold rounded-md hover:bg-purple-50" style={{ color: '#7c3aed' }}>
              <Plus className="w-3 h-3" /> Add Charge
            </button>
          </div>
          {((amendDetails.other_charges || []).length === 0) ? (
            <div className="text-[11px] text-gray-400 italic px-3 py-2 border border-dashed border-gray-200 rounded-lg">No additional charges. Click "Add Charge" to include freight or other line items.</div>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="text-[9px] font-bold text-gray-500 uppercase bg-gray-50">
                    <th className="text-left px-3 py-2">Description</th>
                    <th className="text-right px-3 py-2 w-28">Amount</th>
                    <th className="text-center px-3 py-2 w-24">Taxable (GST)</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {(amendDetails.other_charges || []).map((ch, ci) => (
                    <tr key={ci} className="border-t border-gray-100">
                      <td className="px-3 py-2">
                        <Input
                          data-testid={`amend-charge-desc-${ci}`}
                          value={ch.description || ''}
                          onChange={e => {
                            const charges = [...(amendDetails.other_charges || [])];
                            charges[ci] = { ...charges[ci], description: e.target.value };
                            setAmendDetails(prev => ({ ...prev, other_charges: charges }));
                          }}
                          placeholder="e.g., Freight, Packing"
                          className="text-xs h-8"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          data-testid={`amend-charge-amount-${ci}`}
                          value={ch.amount || 0}
                          min={0}
                          onChange={e => {
                            const charges = [...(amendDetails.other_charges || [])];
                            charges[ci] = { ...charges[ci], amount: Number(e.target.value) || 0 };
                            setAmendDetails(prev => ({ ...prev, other_charges: charges }));
                          }}
                          className="w-full text-right px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-purple-200 focus:outline-none"
                        />
                      </td>
                      <td className="px-3 py-2 text-center">
                        <label className="inline-flex items-center gap-1 cursor-pointer">
                          <input
                            type="checkbox"
                            data-testid={`amend-charge-taxable-${ci}`}
                            checked={ch.taxable !== false}
                            onChange={e => {
                              const charges = [...(amendDetails.other_charges || [])];
                              charges[ci] = { ...charges[ci], taxable: e.target.checked };
                              setAmendDetails(prev => ({ ...prev, other_charges: charges }));
                            }}
                            className="w-3 h-3"
                          />
                          <span className="text-[10px] text-gray-600">+GST</span>
                        </label>
                      </td>
                      <td className="px-3 py-2">
                        <button
                          data-testid={`amend-charge-remove-${ci}`}
                          onClick={() => {
                            const charges = (amendDetails.other_charges || []).filter((_, i) => i !== ci);
                            setAmendDetails(prev => ({ ...prev, other_charges: charges }));
                          }}
                          className="p-0.5 rounded hover:bg-red-50"
                        >
                          <X className="w-3 h-3 text-red-400" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Proposed Total Summary */}
        {amendOC && (() => {
          const sub = amendItems.reduce((s, i) => s + Math.round((i.qty || 0) * (i.rate || 0) * (1 - (i.discount || 0) / 100)), 0);
          const charges = (amendDetails.other_charges || []).reduce((s, c) => s + (Number(c.amount) || 0), 0);
          const taxableCharges = (amendDetails.other_charges || []).filter(c => c.taxable !== false).reduce((s, c) => s + (Number(c.amount) || 0), 0);
          const rate = amendOC.gst_rate || 18;
          const gst = Math.round((sub + taxableCharges) * rate / 100);
          const total = sub + charges + gst;
          return (
            <div className="bg-purple-50 rounded-lg p-3 text-xs border border-purple-100" data-testid="amend-totals-preview">
              <div className="flex justify-between text-gray-700 mb-0.5"><span>Subtotal</span><span className="font-semibold">INR {sub.toLocaleString('en-IN')}</span></div>
              {(amendDetails.other_charges || []).map((c, i) => (
                <div key={i} className="flex justify-between text-gray-600 mb-0.5">
                  <span>{c.description || 'Other Charge'}{c.taxable !== false ? ' (+GST)' : ''}</span>
                  <span>INR {(Number(c.amount) || 0).toLocaleString('en-IN')}</span>
                </div>
              ))}
              <div className="flex justify-between text-gray-700 mb-0.5"><span>GST ({rate}%)</span><span>INR {gst.toLocaleString('en-IN')}</span></div>
              <div className="flex justify-between font-bold text-purple-700 border-t border-purple-200 mt-1 pt-1"><span>Proposed Total</span><span data-testid="amend-proposed-total">INR {total.toLocaleString('en-IN')}</span></div>
            </div>
          );
        })()}

        {/* Additional Details Section */}
        {amendDetails && (
          <div>
            <label className="text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-2 block">Other Details</label>
            <div className="border border-gray-200 rounded-lg p-3 space-y-3">
              {/* Ship To */}
              <div>
                <label className="text-[10px] font-semibold text-gray-500 mb-1 block">Ship To Address</label>
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Name" value={amendDetails.ship_to?.name || ''} onChange={e => updateShipTo('name', e.target.value)} className="text-xs" />
                  <Input placeholder="Address" value={amendDetails.ship_to?.address || ''} onChange={e => updateShipTo('address', e.target.value)} className="text-xs" />
                  <Input placeholder="City" value={amendDetails.ship_to?.city || ''} onChange={e => updateShipTo('city', e.target.value)} className="text-xs" />
                  <Input placeholder="State" value={amendDetails.ship_to?.state || ''} onChange={e => updateShipTo('state', e.target.value)} className="text-xs" />
                  <Input placeholder="Pincode" value={amendDetails.ship_to?.pincode || ''} onChange={e => updateShipTo('pincode', e.target.value)} className="text-xs" />
                  <Input placeholder="GSTIN" value={amendDetails.ship_to?.gstin || ''} onChange={e => updateShipTo('gstin', e.target.value)} className="text-xs" />
                </div>
              </div>
              {/* Terms */}
              <div>
                <label className="text-[10px] font-semibold text-gray-500 mb-1 block">Terms & Conditions</label>
                <div className="space-y-2">
                  <div>
                    <label className="text-[9px] text-gray-400">Payment Terms</label>
                    <Input value={amendDetails.terms?.payment_terms || ''} onChange={e => updateTerms('payment_terms', e.target.value)} className="text-xs" />
                  </div>
                  <div>
                    <label className="text-[9px] text-gray-400">Delivery Schedule</label>
                    <Input value={amendDetails.terms?.delivery_schedule || ''} onChange={e => updateTerms('delivery_schedule', e.target.value)} className="text-xs" />
                  </div>
                  <div>
                    <label className="text-[9px] text-gray-400">Warranty</label>
                    <Input value={amendDetails.terms?.warranty || ''} onChange={e => updateTerms('warranty', e.target.value)} className="text-xs" />
                  </div>
                  <div>
                    <label className="text-[9px] text-gray-400">Freight</label>
                    <Input value={amendDetails.terms?.freight || ''} onChange={e => updateTerms('freight', e.target.value)} className="text-xs" />
                  </div>
                </div>
              </div>
              {/* Remarks */}
              <div>
                <label className="text-[10px] font-semibold text-gray-500 mb-1 block">Remarks</label>
                <Textarea rows={2} value={amendDetails.remarks || ''} onChange={e => updateDetail('remarks', e.target.value)} className="text-xs" placeholder="General remarks..." />
              </div>
            </div>
          </div>
        )}

        {/* Reason / Notes */}
        <div>
          <label className="text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1 block">
            Reason for Amendment <span className="text-red-500">*</span>
          </label>
          <Textarea
            rows={3}
            value={amendReason}
            onChange={e => setAmendReason(e.target.value)}
            placeholder="Explain why this OC needs to be amended (e.g., customer requested qty change, rate correction, address change, etc.)"
            data-testid="amend-reason"
            className="text-xs"
          />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button onClick={onSubmit} disabled={amendLoading || !amendReason.trim()} data-testid="submit-amendment-btn"
          className="bg-purple-600 hover:bg-purple-700 gap-1.5">
          {amendLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Edit3 className="w-3.5 h-3.5" />}
          Submit for Approval
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  );
};
